import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  LayoutDashboard,
  Users,
  UserCheck,
  Dumbbell,
  BookOpen,
  UtensilsCrossed,
  Calendar,
  CreditCard,
  Settings,
  ArrowLeft,
  ShieldCheck,
  ChevronRight,
} from "lucide-react";

const MENU_ITEMS = [
  { id: "overview", label: "Command Center", subtitle: "Overview & key metrics", icon: LayoutDashboard },
  { id: "members", label: "Members", subtitle: "Accounts, tiers & roles", icon: Users },
  { id: "trainers", label: "Trainers", subtitle: "Profiles & approvals", icon: UserCheck },
  { id: "programs", label: "Workout Plans", subtitle: "Templates & programs", icon: Dumbbell },
  { id: "exercises", label: "Exercise Library", subtitle: "Media & instructions", icon: BookOpen },
  { id: "nutrition", label: "Nutrition", subtitle: "Meal plans & macros", icon: UtensilsCrossed },
  { id: "sessions", label: "Appointments", subtitle: "Bookings & scheduling", icon: Calendar },
  { id: "subscriptions", label: "Subscriptions", subtitle: "Membership tiers & billing", icon: CreditCard },
  { id: "settings", label: "Settings", subtitle: "Platform info & admins", icon: Settings },
];

export default function AdminMenuDrawer({
  open,
  onOpenChange,
  activeSection,
  onSelectSection,
  onExitAdmin,
  user,
}) {
  const handleItemClick = (id) => {
    onSelectSection(id);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[85vw] max-w-sm bg-[#030A17] border-l border-white/10 p-0 text-white flex flex-col h-full z-50"
      >
        {/* Header / Admin Info */}
        <div className="p-4 pt-[calc(1rem+env(safe-area-inset-top))] border-b border-white/10 bg-[#071122]">
          <SheetHeader className="text-left">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 rounded-lg bg-sky-500/20 text-sky-400 border border-sky-400/30">
                <ShieldCheck className="w-5 h-5 text-sky-400" />
              </div>
              <div>
                <SheetTitle className="text-white text-base font-bold">Admin Navigation</SheetTitle>
                <p className="text-[11px] text-sky-400 font-semibold tracking-wider uppercase">
                  Management Console
                </p>
              </div>
            </div>
            <div className="rounded-xl bg-white/5 p-2.5 border border-white/5">
              <p className="text-xs font-semibold text-white truncate">
                {user?.full_name || "Administrator"}
              </p>
              <p className="text-[11px] text-slate-400 truncate">{user?.email}</p>
            </div>
          </SheetHeader>
        </div>

        {/* Scrollable Nav Items */}
        <div className="flex-1 overflow-y-auto py-2 px-3 space-y-1">
          {MENU_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleItemClick(item.id)}
                className={`w-full flex items-center justify-between p-2.5 rounded-xl transition-all cursor-pointer text-left ${
                  isActive
                    ? "bg-sky-500/20 text-white border border-sky-400/40 shadow-[0_0_15px_rgba(56,189,248,0.15)]"
                    : "text-slate-300 hover:bg-white/5 active:scale-[0.99]"
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                      isActive
                        ? "bg-sky-500 text-slate-950 font-bold"
                        : "bg-white/5 text-sky-400"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className={`text-xs font-bold truncate ${isActive ? "text-sky-300" : "text-white"}`}>
                      {item.label}
                    </p>
                    <p className="text-[10px] text-slate-400 truncate">{item.subtitle}</p>
                  </div>
                </div>
                <ChevronRight className={`w-4 h-4 shrink-0 ${isActive ? "text-sky-400" : "text-slate-500"}`} />
              </button>
            );
          })}
        </div>

        {/* Footer: Exit Admin */}
        <div className="p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] border-t border-white/10 bg-[#071122]">
          <button
            type="button"
            onClick={() => {
              onOpenChange(false);
              onExitAdmin();
            }}
            className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-300 font-semibold text-xs active:scale-95 transition-all cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4 text-red-400" />
            <span>Exit Admin Mode</span>
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
